import React from 'react'
import { Box, TextField, FormControl, InputLabel, Select, MenuItem } from '@mui/material'
import type { DovolenaRequestType } from '../types'
import type { DovolenaSortKey } from '../lib/dovolenaListFilters'
import { DOVOLENA_REQUEST_TYPES, labelDovolenaRequestType } from '../lib/dovolenaRequestType'

export type DovolenaRequestsToolbarProps = {
  nameQuery: string
  onNameQueryChange: (value: string) => void
  createdFrom: string
  onCreatedFromChange: (value: string) => void
  createdTo: string
  onCreatedToChange: (value: string) => void
  leaveFrom: string
  onLeaveFromChange: (value: string) => void
  leaveTo: string
  onLeaveToChange: (value: string) => void
  sortKey: DovolenaSortKey
  onSortKeyChange: (value: DovolenaSortKey) => void
  sortDir: 'asc' | 'desc'
  onSortDirChange: (value: 'asc' | 'desc') => void
  disabled?: boolean
  extendedFilters?: boolean
  typeFilter?: 'all' | DovolenaRequestType
  onTypeFilterChange?: (value: 'all' | DovolenaRequestType) => void
  statusFilter?: 'all' | 'new' | 'approved' | 'rejected'
  onStatusFilterChange?: (value: 'all' | 'new' | 'approved' | 'rejected') => void
  processedFilter?: 'all' | 'processed' | 'unprocessed'
  onProcessedFilterChange?: (value: 'all' | 'processed' | 'unprocessed') => void
  showProcessedFilter?: boolean
}

export const DovolenaRequestsToolbar: React.FC<DovolenaRequestsToolbarProps> = ({
  nameQuery,
  onNameQueryChange,
  createdFrom,
  onCreatedFromChange,
  createdTo,
  onCreatedToChange,
  leaveFrom,
  onLeaveFromChange,
  leaveTo,
  onLeaveToChange,
  sortKey,
  onSortKeyChange,
  sortDir,
  onSortDirChange,
  disabled = false,
  extendedFilters = false,
  typeFilter = 'all',
  onTypeFilterChange,
  statusFilter = 'all',
  onStatusFilterChange,
  processedFilter = 'all',
  onProcessedFilterChange,
  showProcessedFilter = false,
}) => (
  <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 2, mb: 2, alignItems: 'center' }}>
    <TextField
      size="small"
      label="Jméno / příjmení / os. číslo"
      value={nameQuery}
      onChange={(e) => onNameQueryChange(e.target.value)}
      disabled={disabled}
      sx={{ minWidth: 220, flex: '1 1 200px' }}
    />
    <TextField
      size="small"
      type="date"
      label="Podáno od"
      value={createdFrom}
      onChange={(e) => onCreatedFromChange(e.target.value)}
      disabled={disabled}
      InputLabelProps={{ shrink: true }}
      sx={{ minWidth: 150 }}
    />
    <TextField
      size="small"
      type="date"
      label="Podáno do"
      value={createdTo}
      onChange={(e) => onCreatedToChange(e.target.value)}
      disabled={disabled}
      InputLabelProps={{ shrink: true }}
      sx={{ minWidth: 150 }}
    />
    <TextField
      size="small"
      type="date"
      label="Dovolená od"
      value={leaveFrom}
      onChange={(e) => onLeaveFromChange(e.target.value)}
      disabled={disabled}
      InputLabelProps={{ shrink: true }}
      sx={{ minWidth: 150 }}
    />
    <TextField
      size="small"
      type="date"
      label="Dovolená do"
      value={leaveTo}
      onChange={(e) => onLeaveToChange(e.target.value)}
      disabled={disabled}
      InputLabelProps={{ shrink: true }}
      sx={{ minWidth: 150 }}
    />
    <FormControl size="small" sx={{ minWidth: 180 }} disabled={disabled}>
      <InputLabel id="dov-sort-key">Řadit podle</InputLabel>
      <Select
        labelId="dov-sort-key"
        label="Řadit podle"
        value={sortKey}
        onChange={(e) => onSortKeyChange(e.target.value as DovolenaSortKey)}
      >
        <MenuItem value="requester_name">Jméno žadatele</MenuItem>
        <MenuItem value="created_at">Datum podání</MenuItem>
        <MenuItem value="date_from">Datum od (dovolená)</MenuItem>
        <MenuItem value="date_to">Datum do (dovolená)</MenuItem>
        {extendedFilters && <MenuItem value="status">Stav</MenuItem>}
      </Select>
    </FormControl>
    <FormControl size="small" sx={{ minWidth: 140 }} disabled={disabled}>
      <InputLabel id="dov-sort-dir">Pořadí</InputLabel>
      <Select
        labelId="dov-sort-dir"
        label="Pořadí"
        value={sortDir}
        onChange={(e) => onSortDirChange(e.target.value as 'asc' | 'desc')}
      >
        <MenuItem value="desc">Sestupně</MenuItem>
        <MenuItem value="asc">Vzestupně</MenuItem>
      </Select>
    </FormControl>
    {extendedFilters && onTypeFilterChange && (
      <FormControl size="small" sx={{ minWidth: 160 }} disabled={disabled}>
        <InputLabel id="dov-type-filter">Typ</InputLabel>
        <Select
          labelId="dov-type-filter"
          label="Typ"
          value={typeFilter}
          onChange={(e) => onTypeFilterChange(e.target.value as 'all' | DovolenaRequestType)}
        >
          <MenuItem value="all">Všechny typy</MenuItem>
          {DOVOLENA_REQUEST_TYPES.map((rt) => (
            <MenuItem key={rt} value={rt}>
              {labelDovolenaRequestType(rt)}
            </MenuItem>
          ))}
        </Select>
      </FormControl>
    )}
    {extendedFilters && onStatusFilterChange && (
      <FormControl size="small" sx={{ minWidth: 140 }} disabled={disabled}>
        <InputLabel id="dov-status-filter">Stav</InputLabel>
        <Select
          labelId="dov-status-filter"
          label="Stav"
          value={statusFilter}
          onChange={(e) =>
            onStatusFilterChange(e.target.value as 'all' | 'new' | 'approved' | 'rejected')
          }
        >
          <MenuItem value="all">Vše</MenuItem>
          <MenuItem value="new">Nové</MenuItem>
          <MenuItem value="approved">Schválené</MenuItem>
          <MenuItem value="rejected">Zamítnuté</MenuItem>
        </Select>
      </FormControl>
    )}
    {extendedFilters && showProcessedFilter && onProcessedFilterChange && (
      <FormControl size="small" sx={{ minWidth: 180 }} disabled={disabled}>
        <InputLabel id="dov-processed-filter">Zpracování</InputLabel>
        <Select
          labelId="dov-processed-filter"
          label="Zpracování"
          value={processedFilter}
          onChange={(e) =>
            onProcessedFilterChange(e.target.value as 'all' | 'processed' | 'unprocessed')
          }
        >
          <MenuItem value="all">Vše</MenuItem>
          <MenuItem value="unprocessed">Nezpracované</MenuItem>
          <MenuItem value="processed">Zpracované</MenuItem>
        </Select>
      </FormControl>
    )}
  </Box>
)
